<template>
  <div
    :id="`sequence_analytics_${sequenceId}`"
    class="flex flex-col gap-4 pr-4"
  >
    <div v-if="!isInitializing" class="flex items-center justify-between gap-3">
      <SequenceAnalyticsFilterComponent :allowed-filters="['user', 'date']" />

      <CustomDropdown maxHeight="200px" gap="10px" position="end">
        <template #trigger>
          <div
            class="flex items-center justify-center p-2 rounded-lg bg-[--white-opacity-2] border_8 cursor-pointer hover:bg-[--white-opacity-4] hover:border_12"
          >
            <ThemeImage
              class="size-4 cursor-pointer"
              :icon="imageNames.MORE_VERTICAL"
              alt="more_options"
            />
          </div>
        </template>

        <template #default>
          <div
            class="flex items-center flex-row gap-2 px-3 py-2 rounded-lg dropdown_item cursor-pointer"
            @click="handleExportCSV"
          >
            <ThemeImage
              class="size-4"
              :icon="imageNames.EXPORT_GREY"
              alt="export_icon"
            />
            <span class="proxima_nova text-sm">Export CSV</span>
          </div>
        </template>
      </CustomDropdown>
    </div>

    <div class="flex flex-col gap-8 mb-3">
      <SequenceAnalyticsGrid />

      <div
        v-for="(component, index) in visibleComponents"
        :key="`${component.id}_component_${index}`"
        class="flex flex-col gap-3"
      >
        <span class="text-lg font-semibold">{{ component.text }}</span>
        <component
          :is="component.component"
          :sequence-id="sequenceId"
          :activity-type="component.id"
        />
      </div>
    </div>

    <BulkExportModal
      source="sequence_analytics_individual"
      :visible="showExportModal"
      title="Export Sequence Analytics"
      description="Sequence analytics will be exported to your email"
      :selectCount="0"
      @update:visible="(val) => (showExportModal = val)"
      @confirm="handleExportConfirm"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { message } from "ant-design-vue";
import { useStore } from "vuex";
import SequenceAnalyticsFilterComponent from "@/src/views/sequence/analytics/SequenceAnalyticsFilterComponent.vue";
import SequenceAnalyticsGrid from "@/src/views/analytics/v2/sequence_analytics/SequenceAnalyticsGrid.vue";
import SequenceTasksAnalytics from "@/src/views/analytics/v2/sequence_analytics/tasks/SequenceTasksAnalytics.vue";
import SequenceEmailAnalytics from "@/src/views/analytics/v2/sequence_analytics/email/SequenceEmailAnalytics.vue";
import SequenceLinkedinAnalytics from "@/src/views/analytics/v2/sequence_analytics/linkedin/SequenceLinkedinAnalytics.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import BulkExportModal from "@/src/components/custom_table_components/BulkExportModal.vue";

const store = useStore();
const isInitializing = ref(true);
const showExportModal = ref(false);
const isLinkedinEnabled = localStorage.getItem("linkedin_blocks") === "true"; 

const props = defineProps({
  sequenceId: { type: [String, Number], required: true },
});

const components = ref([
  {
    id: "email_activity_analytics",
    text: "Email",
    component: SequenceEmailAnalytics,
    show: true,
  },
  {
    id: "linkedin_activity_analytics",
    text: "LinkedIn",
    component: SequenceLinkedinAnalytics,
    show: isLinkedinEnabled,
  },
  {
    id: "tasks_activity_analytics",
    text: "Tasks",
    component: SequenceTasksAnalytics,
    show: true,
  },
]);

const visibleComponents = computed(() =>
  components.value.filter((component) => component.show),
);

const filters = computed(() => store.getters["analytics/getFilters"]);

const initializeData = async () => {
  try {
    const response = await DashboardService.fetchUsers();
    if (response?.success) {
      await store.dispatch("analytics/updateUsersList", response.users);
    }
  } catch (error) {
    console.error("Error initializing data:", error);
    message.error("Failed to load users list");
  } finally {
    isInitializing.value = false;
  }
};

const handleExportCSV = () => {
  // Show the modal to collect email address
  showExportModal.value = true;
};

const handleExportConfirm = async ({ email }) => {
  try {
    const range = store.getters["analytics/getRange"];
    const userIds = store.getters["analytics/getUserIds"];
    const teamIds = store.getters["analytics/getTeamIds"];

    message.loading({ content: "Initiating export...", key: "exportCSV" });

    // Build filters object
    const exportFilters = {};

    if (userIds && userIds.length > 0) {
      exportFilters.user_ids = userIds;
    }
    if (teamIds && teamIds.length > 0) {
      exportFilters.team_ids = teamIds;
    }
    if (range?.start) {
      exportFilters.start_time = range.start;
    }
    if (range?.end) {
      exportFilters.end_time = range.end;
    }

    // Get browser timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Call SuperSales API to initiate export for this specific sequence
    const response = await AnalyticService.V2.exportSequenceAnalytics({
      filters: exportFilters,
      export_name: `Sequence ${props.sequenceId} Analytics`,
      browser_timezone: browserTimezone,
      sequence_id: props.sequenceId, // Only export this sequence
      export_email: email, // Add email from modal
    });

    if (response.success) {
      message.success({
        content: "Export initiated successfully",
        key: "exportCSV",
        duration: 5,
      });
      showExportModal.value = false; // Close modal on success
    } else {
      // Show specific error from backend
      const errorMessage = response.error || "Failed to initiate export";
      message.error({
        content: errorMessage,
        key: "exportCSV",
        duration: 5,
      });
    }
  } catch (error) {
    console.error("Error initiating export:", error);
    // Show specific error if available
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      "Failed to initiate export. Please try again.";
    message.error({
      content: errorMessage,
      key: "exportCSV",
      duration: 5,
    });
  }
};

watch(
  () => props.sequenceId,
  (newId) => {
    if (newId) {
      // Set the sequence filter in the store when sequenceId changes
      const currentFilters = filters.value || {};
      store.dispatch("analytics/updateFilters", {
        ...currentFilters,
        sequence: { id: newId },
      });
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await initializeData();
  if (props.sequenceId) {
    // Initialize the sequence filter
    const currentFilters = filters.value || {};
    await store.dispatch("analytics/updateFilters", {
      ...currentFilters,
      sequence: { id: props.sequenceId },
    });
  }
});
</script>
